import React, { useEffect, useState, useCallback, memo } from 'react';
import {
    Autocomplete,
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    IconButton,
    Grid,
    Alert,
    CircularProgress,
    AlertTitle,
    Typography,
    Divider,
    Paper,
    Chip,
    Stack,
    useTheme,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WorkIcon from '@mui/icons-material/Work';
import StarIcon from '@mui/icons-material/Star';
import { addRatingData, getDropdownData, getProgramCodesFromClients } from '../../Queries';

// Memoized FormSection component to prevent re-renders
const FormSection = memo(({ title, icon, children }) => {
    const theme = useTheme();
    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 3, 
                mb: 3, 
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                {icon}
                <Typography variant="h6" color="primary" fontWeight="600" sx={{ fontSize: '0.95rem' }}>
                    {title}
                </Typography>
            </Stack>
            <Divider sx={{ mb: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1) }} />
            {children}
        </Paper>
    );
});


const RatingForm = ({ handleRefresh, editMode = false, editData = null, onEditComplete = null }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({
        open: false,
        severity: 'success', // 'success' or 'error'
        message: '',
    });
    const [name, setName] = useState('');
    const [programCode, setProgramCode] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [learningGrowth, setLearningGrowth] = useState([]);
    const [taskOwnership, setTaskOwnership] = useState([]);
    const [problemSolving, setProblemSolving] = useState([]);
    const [codeQuality, setCodeQuality] = useState([]);
    const [communication, setCommunication] = useState([]);
    const [professionalHabits, setProfessionalHabits] = useState([]);
    const [selectedLearningGrowth, setSelectedLearningGrowth] = useState([]);
    const [selectedTaskOwnership, setSelectedTaskOwnership] = useState([]);
    const [selectedProblemSolving, setSelectedProblemSolving] = useState([]);
    const [selectedCodeQuality, setSelectedCodeQuality] = useState([]);
    const [selectedCommunication, setSelectedCommunication] = useState([]);
    const [selectedProfessionalHabits, setSelectedProfessionalHabits] = useState([]);

    const [programCodes, setProgramCodes] = useState([]);
    const [formId, setFormId] = useState(new Date().getTime());
    const currentUser = localStorage.getItem("user");
    const [isLoading, setIsLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(editMode);
    const [originalFormId, setOriginalFormId] = useState(null);
    const [formErrors, setFormErrors] = useState({
        name: '',
        startDate: '',
        endDate: '',
        programCode: '',
        questions: ''
    });

    // Memoized change handlers to prevent unnecessary re-renders
    const handleNameChange = useCallback((e) => {
        setName(e.target.value);
    }, []);

    const handleStartDateChange = useCallback((e) => {
        setStartDate(e.target.value);
    }, []);

    const handleEndDateChange = useCallback((e) => {
        setEndDate(e.target.value);
    }, []);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleSuccess = (message) => {
        setAlert({
            open: true,
            severity: 'success',
            message,
        });
        setIsLoading(false);
    };

    const handleError = (message) => {
        setAlert({
            open: true,
            severity: 'error',
            message,
        });
        setIsLoading(false);
    };

    const clearForm = useCallback(() => {
        setName('');
        setSelectedLearningGrowth([]);
        setSelectedTaskOwnership([]);
        setSelectedProblemSolving([]);
        setSelectedCodeQuality([]);
        setSelectedCommunication([]);
        setSelectedProfessionalHabits([]);
        setProgramCode(null);
        setStartDate('');
        setEndDate('');
    }, []);

    const loadEditData = useCallback(() => {
        if (editMode && editData) {
            setName(editData.name || '');
            setStartDate(editData.start_date || '');
            setEndDate(editData.end_date || '');
            setProgramCode(editData.program_code || null);
            setOriginalFormId(editData.form_id || editData.id);
            
            // Load questions if available
            if (editData.questions) {
                try {
                    const questions = typeof editData.questions === 'string' 
                        ? JSON.parse(editData.questions) 
                        : editData.questions;
                    
                    setSelectedLearningGrowth(questions.learning_skill_growth || []);
                    setSelectedTaskOwnership(questions.task_ownership_delivery || []);
                    setSelectedProblemSolving(questions.problem_solving_thinking || []);
                    setSelectedCodeQuality(questions.code_quality_engineering || []);
                    setSelectedCommunication(questions.communication_collaboration || []);
                    setSelectedProfessionalHabits(questions.professional_habits || []);
                } catch (error) {
                    console.error('Error parsing questions:', error);
                }
            }
        }
    }, [editMode, editData]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            return;
        }
        
        try {
            const questions = {
                learning_skill_growth: selectedLearningGrowth,
                task_ownership_delivery: selectedTaskOwnership,
                problem_solving_thinking: selectedProblemSolving,
                code_quality_engineering: selectedCodeQuality,
                communication_collaboration: selectedCommunication,
                professional_habits: selectedProfessionalHabits
            };

            setIsLoading(true);
            let formData = {
                id: isEditMode ? originalFormId.toString() : formId.toString(),
                name: name,
                start_date: startDate.toString(),
                end_date: endDate.toString(),
                program_code: programCode,
                json: isEditMode ? (editData?.json || "") : "",
                created_at: isEditMode ? (editData?.created_at || new Date().toLocaleDateString().toString()) : new Date().toLocaleDateString().toString(),
                updated_at: new Date().toLocaleDateString().toString(),
                created_by: isEditMode ? (editData?.created_by || currentUser) : currentUser,
                questions: JSON.stringify(questions)
            }
            
            let response = await addRatingData(formData, isEditMode);
            if (response.data && response.data.statusCode === 200) {
                let result = JSON.parse(response.data.body);
                
                if (isEditMode) {
                    // Handle edit mode completion
                    if (onEditComplete) {
                        onEditComplete(result);
                        // Don't show success alert here, let parent handle it
                    } else {
                        handleSuccess(result.message || 'Form updated successfully!');
                    }
                } else {
                    // Handle create mode completion
                    setFormId(new Date().getTime());
                    clearForm();
                    handleSuccess(result.message || 'Form created successfully!');
                }
                
                // Always refresh the parent table data
                if (handleRefresh) {
                    handleRefresh();
                }
            }
            else {
                handleError(isEditMode ? 'Unable to update the form. Please try again.' : 'Unable to add the form. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            handleError(isEditMode ? 'Unable to update the form. Please try again.' : 'Unable to add the form. Please try again.');
        }

    };

    const getDropDowns = async () => {
        try {
            const [dropdownData, programCodesData] = await Promise.all([
                getDropdownData(),
                getProgramCodesFromClients()
            ]);

            const newLearningGrowth = dropdownData.filter(item => item.type === "learning_skill_growth").map(item => item.name);
            const newTaskOwnership = dropdownData.filter(item => item.type === "task_ownership_delivery").map(item => item.name);
            const newProblemSolving = dropdownData.filter(item => item.type === "problem_solving_thinking").map(item => item.name);
            const newCodeQuality = dropdownData.filter(item => item.type === "code_quality_engineering").map(item => item.name);
            const newCommunication = dropdownData.filter(item => item.type === "communication_collaboration").map(item => item.name);
            const newProfessionalHabits = dropdownData.filter(item => item.type === "professional_habits").map(item => item.name);
            const newProgramCodes = programCodesData.map(item => item.name);

            setLearningGrowth(newLearningGrowth);
            setTaskOwnership(newTaskOwnership);
            setProblemSolving(newProblemSolving);
            setCodeQuality(newCodeQuality);
            setCommunication(newCommunication);
            setProfessionalHabits(newProfessionalHabits);
            setProgramCodes(newProgramCodes);

            // Update selected values to only include items that still exist in the dropdown
            setSelectedLearningGrowth(prev => prev.filter(item => newLearningGrowth.includes(item)));
            setSelectedTaskOwnership(prev => prev.filter(item => newTaskOwnership.includes(item)));
            setSelectedProblemSolving(prev => prev.filter(item => newProblemSolving.includes(item)));
            setSelectedCodeQuality(prev => prev.filter(item => newCodeQuality.includes(item)));
            setSelectedCommunication(prev => prev.filter(item => newCommunication.includes(item)));
            setSelectedProfessionalHabits(prev => prev.filter(item => newProfessionalHabits.includes(item)));

            return Promise.resolve();
        } catch (error) {
            console.error('An error occurred while fetching dropdown data.', error);
            return Promise.reject(error);
        }
    };

    // Expose refresh method for external use
    const refreshDropdowns = useCallback(async () => {
        try {
            await getDropDowns();
        } catch (error) {
            console.error('Error refreshing dropdown data:', error);
        }
    }, []);

    const handleCloseAlert = useCallback(() => {
        setAlert({
            ...alert,
            open: false,
        });
        setOpen(false);
    }, [alert]);

    useEffect(() => {
        getDropDowns();
    }, []);

    // Refresh dropdown data when dialog opens
    useEffect(() => {
        if (open) {
            getDropDowns();
        }
    }, [open]);

    useEffect(() => {
        if (editMode && editData) {
            // Refresh dropdown data first, then load edit data
            getDropDowns().then(() => {
                loadEditData();
            });
        }
    }, [editMode, editData, loadEditData]);

    const validateForm = () => {
        const errors = {
            name: '',
            startDate: '',
            endDate: '',
            programCode: '',
            questions: ''
        };

        let isValid = true;

        // Validate name
        if (!name.trim()) {
            errors.name = 'Form name is required';
            isValid = false;
        } else if (name.length < 3) {
            errors.name = 'Form name must be at least 3 characters';
            isValid = false;
        }

        // Validate start date
        if (!startDate) {
            errors.startDate = 'Start date is required';
            isValid = false;
        }

        // Validate end date
        if (!endDate) {
            errors.endDate = 'End date is required';
            isValid = false;
        } else if (startDate && new Date(endDate) <= new Date(startDate)) {
            errors.endDate = 'End date must be after start date';
            isValid = false;
        }

        // Validate program code
        if (!programCode) {
            errors.programCode = 'Program code is required';
            isValid = false;
        }

        // Validate questions
        const hasQuestions = selectedLearningGrowth.length > 0 || 
                           selectedTaskOwnership.length > 0 || 
                           selectedProblemSolving.length > 0 || 
                           selectedCodeQuality.length > 0 || 
                           selectedCommunication.length > 0 || 
                           selectedProfessionalHabits.length > 0;
        
        if (!hasQuestions) {
            errors.questions = 'At least one question must be selected';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    // Reset form when switching between modes
    useEffect(() => {
        setIsEditMode(editMode);
        if (!editMode) {
            clearForm();
            setOpen(false);
        } else {
            // Auto-open dialog in edit mode
            setOpen(true);
        }
    }, [editMode, clearForm, editData]);

    // Cleanup effect - reset open state when component unmounts
    useEffect(() => {
        return () => {
            setOpen(false);
        };
    }, []);

    return (
        <>
            <Container>
                <Dialog 
                    key={`dialog-${isEditMode ? editData?.id || 'edit' : 'create'}-${formId}`}
                    open={open} 
                    onClose={handleClose} 
                    maxWidth="lg" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            maxHeight: '90vh',
                        }
                    }}
                >
                    {isLoading && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                p: 4,
                                minHeight: '200px'
                            }}
                        >
                            <CircularProgress size={48} thickness={4} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.8rem' }}>
                                Saving evaluation...
                            </Typography>
                        </Box>
                    )}
                    
                    {alert.open && (
                        <Alert 
                            severity={alert.severity} 
                            onClose={handleCloseAlert}
                            sx={{ 
                                m: 2,
                                borderRadius: 2,
                                '& .MuiAlert-message': {
                                    fontSize: '1rem'
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 'bold' }}>
                                {alert.severity === 'success' ? 'Success!' : 'Error'}
                            </AlertTitle>
                            {alert.message}
                        </Alert>
                    )}
                    
                    {!isLoading && !alert.open && (
                        <>
                            <DialogTitle sx={{ 
                                background: `linear-gradient(135deg, #0a0068 0%, #0c0082 100%)`,
                                color: 'white',
                                position: 'relative',
                                py: 3
                            }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <StarIcon sx={{ fontSize: 28 }} />
                                    <Typography variant="h6" component="div" fontWeight="600" sx={{ fontSize: '1rem' }}>
                                        {isEditMode ? 'Edit Self-Reflection Form' : 'Create Self-Reflection Form'}
                                    </Typography>
                                </Stack>
                                <IconButton 
                                    onClick={handleClose} 
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 12, 
                                        right: 12,
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: alpha('#ffffff', 0.1)
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            
                            <DialogContent sx={{ p: 0 }}>
                                <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
                                    {/* Basic Information Section */}
                                    <FormSection 
                                        title="Basic Information" 
                                        icon={<PersonIcon color="primary" />}
                                    >
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    key="employee-name-field"
                                                    id="name"
                                                    label="Form Name"
                                                    variant="outlined"
                                                    size="small"
                                                    InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                    InputProps={{ style: { fontSize: '0.8rem' } }}
                                                    fullWidth
                                                    value={name}
                                                    onChange={handleNameChange}
                                                    required
                                                    autoComplete="off"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    key="start-date-field"
                                                    id="start-date"
                                                    label="Self-Reflection Start Date"
                                                    type="date"
                                                    variant="outlined"
                                                    size="small"
                                                    InputLabelProps={{ style: { fontSize: '0.8rem' }, shrink: true }}
                                                    InputProps={{ style: { fontSize: '0.8rem' } }}
                                                    fullWidth
                                                    value={startDate}
                                                    onChange={handleStartDateChange}
                                                    autoComplete="off"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    key="end-date-field"
                                                    id="end-date"
                                                    label="Self-Reflection End Date"
                                                    type="date"
                                                    variant="outlined"
                                                    size="small"
                                                    InputLabelProps={{ style: { fontSize: '0.8rem' }, shrink: true }}
                                                    InputProps={{ style: { fontSize: '0.8rem' } }}
                                                    fullWidth
                                                    value={endDate}
                                                    onChange={handleEndDateChange}
                                                    autoComplete="off"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                                    <Autocomplete
                                                        multiple={false}
                                                        id="program-code"
                                                        options={programCodes}
                                                        value={programCode}
                                                        onChange={(event, newValue) => setProgramCode(newValue)}
                                                        getOptionLabel={(option) => option}
                                                        size="small"
                                                        sx={{ flex: 1 }}
                                                        renderInput={(params) => (
                                                            <TextField 
                                                                {...params} 
                                                                label="Program Code" 
                                                                variant="outlined" 
                                                                size="small"
                                                                fullWidth
                                                                InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                                InputProps={{ 
                                                                    ...params.InputProps,
                                                                    style: { fontSize: '0.8rem' } 
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                    },
                                                                    '& .MuiAutocomplete-popupIndicator': {
                                                                        fontSize: '1rem'
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    {isEditMode && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={refreshDropdowns}
                                                            sx={{
                                                                borderRadius: 2,
                                                                px: 2,
                                                                py: 1,
                                                                minWidth: 'auto',
                                                                fontSize: '0.8rem',
                                                                textTransform: 'none',
                                                                borderColor: theme.palette.primary.main,
                                                                color: theme.palette.primary.main,
                                                                '&:hover': {
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                                }
                                                            }}
                                                        >
                                                            Refresh Options
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </FormSection>

                                    {/* Technical Skills Section */}
                                    <FormSection 
                                        title="Technical Competencies" 
                                        icon={<CodeIcon color="primary" />}
                                    >
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    id="code_quality"
                                                    options={codeQuality}
                                                    value={selectedCodeQuality}
                                                    onChange={(event, newValue) => setSelectedCodeQuality(newValue)}
                                                    getOptionLabel={(option) => option}
                                                    size="small"
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                {...getTagProps({ index })}
                                                                color="info"
                                                                size="small"
                                                                sx={{ m: 0.3, fontSize: '0.7rem' }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Code Quality & Engineering Practices" 
                                                            variant="outlined" 
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                            InputProps={{ 
                                                                ...params.InputProps,
                                                                style: { fontSize: '0.8rem' } 
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                                '& .MuiAutocomplete-popupIndicator': {
                                                                    fontSize: '1rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </FormSection>

                                    {/* Professional Skills Section */}
                                    <FormSection 
                                        title="Professional Skills & Behavior" 
                                        icon={<WorkIcon color="primary" />}
                                    >
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    multiple
                                                    id="professional_habits"
                                                    options={professionalHabits}
                                                    value={selectedProfessionalHabits}
                                                    onChange={(event, newValue) => setSelectedProfessionalHabits(newValue)}
                                                    getOptionLabel={(option) => option}
                                                    size="small"
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                {...getTagProps({ index })}
                                                                color="warning"
                                                                size="small"
                                                                sx={{ m: 0.3, fontSize: '0.7rem' }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Professional Habits" 
                                                            variant="outlined" 
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                            InputProps={{ 
                                                                ...params.InputProps,
                                                                style: { fontSize: '0.8rem' } 
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                                '& .MuiAutocomplete-popupIndicator': {
                                                                    fontSize: '1rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    multiple
                                                    id="communication"
                                                    options={communication}
                                                    value={selectedCommunication}
                                                    onChange={(event, newValue) => setSelectedCommunication(newValue)}
                                                    getOptionLabel={(option) => option}
                                                    size="small"
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                {...getTagProps({ index })}
                                                                color="error"
                                                                size="small"
                                                                sx={{ m: 0.3, fontSize: '0.7rem' }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Communication & Collaboration" 
                                                            variant="outlined" 
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                            InputProps={{ 
                                                                ...params.InputProps,
                                                                style: { fontSize: '0.8rem' } 
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                                '& .MuiAutocomplete-popupIndicator': {
                                                                    fontSize: '1rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </FormSection>

                                    {/* Growth & Development Section */}
                                    <FormSection 
                                        title="Growth & Development" 
                                        icon={<PsychologyIcon color="primary" />}
                                    >
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    multiple
                                                    id="learning"
                                                    options={learningGrowth}
                                                    value={selectedLearningGrowth}
                                                    onChange={(event, newValue) => setSelectedLearningGrowth(newValue)}
                                                    getOptionLabel={(option) => option}
                                                    size="small"
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                {...getTagProps({ index })}
                                                                color="primary"
                                                                size="small"
                                                                sx={{ m: 0.3, fontSize: '0.7rem' }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Learning & Skill Growth" 
                                                            variant="outlined" 
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                            InputProps={{ 
                                                                ...params.InputProps,
                                                                style: { fontSize: '0.8rem' } 
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                                '& .MuiAutocomplete-popupIndicator': {
                                                                    fontSize: '1rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    multiple
                                                    id="task_ownership"
                                                    options={taskOwnership}
                                                    value={selectedTaskOwnership}
                                                    onChange={(event, newValue) => setSelectedTaskOwnership(newValue)}
                                                    getOptionLabel={(option) => option}
                                                    size="small"
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                {...getTagProps({ index })}
                                                                color="secondary"
                                                                size="small"
                                                                sx={{ m: 0.3, fontSize: '0.7rem' }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Task Ownership & Delivery" 
                                                            variant="outlined" 
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                            InputProps={{ 
                                                                ...params.InputProps,
                                                                style: { fontSize: '0.8rem' } 
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                                '& .MuiAutocomplete-popupIndicator': {
                                                                    fontSize: '1rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    id="problem_solving"
                                                    options={problemSolving}
                                                    value={selectedProblemSolving}
                                                    onChange={(event, newValue) => setSelectedProblemSolving(newValue)}
                                                    getOptionLabel={(option) => option}
                                                    size="small"
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                {...getTagProps({ index })}
                                                                color="info"
                                                                size="small"
                                                                sx={{ m: 0.3, fontSize: '0.7rem' }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Problem-Solving & Thinking" 
                                                            variant="outlined" 
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                                                            InputProps={{ 
                                                                ...params.InputProps,
                                                                style: { fontSize: '0.8rem' } 
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                                '& .MuiAutocomplete-popupIndicator': {
                                                                    fontSize: '1rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </FormSection>

                                    {/* Submit Button */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-end', 
                                        mt: 4,
                                        pt: 3,
                                        borderTop: `1px solid ${theme.palette.divider}`
                                    }}>
                                        <Button 
                                            type="submit" 
                                            variant="contained" 
                                            size="medium"
                                            sx={{
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                px: 4,
                                                py: 1.5,
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, #0a0068 0%, #0c0082 100%)',
                                                boxShadow: '0 4px 15px rgba(10, 0, 104, 0.3)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #0c0082 0%, #0e009e 100%)',
                                                    boxShadow: '0 6px 20px rgba(10, 0, 104, 0.4)',
                                                    transform: 'translateY(-1px)',
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                            }}
                                        >
                                            {isEditMode ? 'Update Self-Reflection' : 'Create Self-Reflection'}
                                        </Button>
                                    </Box>
                                </form>
                            </DialogContent>
                        </>
                    )}
                </Dialog>
            </Container>
            
            {!isEditMode && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0 }}>
                    <Button 
                        onClick={handleOpen} 
                        variant="contained" 
                        size="small"
                        startIcon={<StarIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #0a0068 0%, #0c0082 100%)',
                            boxShadow: '0 2px 8px rgba(10, 0, 104, 0.2)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0c0082 0%, #0e009e 100%)',
                                boxShadow: '0 4px 12px rgba(10, 0, 104, 0.3)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Create Self-Reflection
                    </Button>
                </Box>
            )}
        </>
    );
};

export default RatingForm;